import { IconDot } from "@/assets/icons/geist/IconDot";
import { cn } from "@/components/podkit/lib/cn";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/podkit/popover/Popover";
import { Pill } from "@/components/podkit/status/Pill";
import { type EffectiveState } from "@/routes/environments/phase";
import { EnvironmentPhase } from "gitpod-next-api/gitpod/v1/environment_pb";
import { ChevronDownIcon } from "lucide-react";
import { useMemo, type FC } from "react";

export const EnvironmentPhaseTag: FC<{
    state: EffectiveState;
    variant?: "dot" | "label";
    truncate?: boolean;
}> = ({ state, variant = "label", truncate }) => {
    const { pillVariant, fgColor, dotBgColor, pulse, name, warnings, failures, timeout } = useMemo(() => {
        if (state.failures && state.state !== EnvironmentPhase.DELETING) {
            return {
                pillVariant: "danger" as const,
                bgColor: "bg-surface-negative/10",
                fgColor: "text-content-negative",
                dotBgColor: "bg-content-negative",
                pulse: false,
                name: "Failed",
                ...state,
            };
        }

        let pillVariant: "success" | "warning" | "danger" | "default" | "info" | "neutral" = "default";
        let dotBgColor = "bg-content-tertiary";
        let fgColor = "text-content-tertiary";
        let name = "Unknown";
        let pulse = false;

        switch (state.state) {
            case EnvironmentPhase.RUNNING:
                pillVariant = "success";
                dotBgColor = "bg-content-positive";
                fgColor = "text-content-positive";
                name = "Running";
                pulse = false;
                if (state.warnings) {
                    pillVariant = "warning";
                    dotBgColor = "bg-content-yield";
                    fgColor = "text-content-yield";
                }
                break;
            case EnvironmentPhase.CREATING:
                pillVariant = "warning";
                dotBgColor = "bg-content-yield";
                fgColor = "text-content-yield";
                name = "Creating...";
                pulse = true;
                break;
            case EnvironmentPhase.STARTING:
                pillVariant = "success";
                dotBgColor = "bg-content-positive";
                fgColor = "text-content-positive";
                name = "Starting...";
                pulse = true;
                break;
            case EnvironmentPhase.UPDATING:
                pillVariant = "warning";
                dotBgColor = "bg-content-yield";
                fgColor = "text-content-yield";
                name = "Updating...";
                pulse = true;
                break;
            case EnvironmentPhase.STOPPING:
                pillVariant = "warning";
                dotBgColor = "bg-content-yield";
                fgColor = "text-content-yield";
                name = "Stopping...";
                if (state.timeout) {
                    name = `Auto-stopping...`;
                }
                pulse = true;
                break;
            case EnvironmentPhase.STOPPED:
                pillVariant = "neutral";
                dotBgColor = "bg-content-tertiary";
                fgColor = "text-content-primary";
                name = "Stopped";
                if (state.timeout) {
                    name = `Auto-stopped`;
                }
                break;
            case EnvironmentPhase.DELETING:
                pillVariant = "danger";
                dotBgColor = "bg-content-negative";
                fgColor = "text-content-negative";
                name = "Deleting...";
                pulse = true;
                break;
        }
        return {
            pillVariant,
            fgColor,
            dotBgColor,
            pulse,
            name,
            ...state,
        };
    }, [state]);

    if (variant === "dot") {
        return (
            <>
                <IconDot size="lg" className={fgColor} />
            </>
        );
    }

    const problems =
        timeout || warnings || failures
            ? [...(timeout ? [timeout] : []), ...(failures || []), ...(warnings || [])]
            : undefined;

    const DotIcon = () => (
        <div
            className={cn("inline-flex h-4 flex-row items-center gap-2 rounded-full text-sm", pulse && "animate-pulse")}
        >
            <div className={cn("size-2 rounded-full", dotBgColor)} />
        </div>
    );

    return (
        <Pill
            variant={pillVariant}
            size="sm"
            LeadingIcon={DotIcon}
            TrailingIcon={
                problems
                    ? () => (
                          <Popover>
                              <PopoverTrigger data-testid="problems-popover-trigger">
                                  <ChevronDownIcon size={16} />
                              </PopoverTrigger>
                              <PopoverContent align="end">
                                  <div className="text-base text-content-secondary">
                                      {problems
                                          .map((m) => m.split("\n"))
                                          .flat()
                                          .map((e, i) => (
                                              <p key={i}>{e}</p>
                                          ))}
                                  </div>
                              </PopoverContent>
                          </Popover>
                      )
                    : undefined
            }
            className={cn(pulse && "animate-pulse")}
            truncate={truncate}
        >
            {name}
        </Pill>
    );
};
